import {
  Component,
  ViewChild,
  ViewContainerRef,
  ChangeDetectorRef,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonsComponent } from '../buttons/buttons.component';
import { BaseModalService } from 'src/utils/base-modal.service';

export interface NodeValidationMessage {
  text: string;
  anchor?: string;
}

export interface NodeValidationStatus {
  type: 'ok' | 'error' | 'warning' | 'idle';
  errorCount?: number;
  warningCount?: number;
  messages?: NodeValidationMessage[];
}

@Component({
  selector: 'app-base-modal',
  standalone: true,
  imports: [CommonModule, ButtonsComponent],
  templateUrl: './base-modal.component.html',
  styleUrl: './base-modal.component.scss',
})
export class BaseModalComponent implements OnDestroy {
  private readonly baseModalService = inject(BaseModalService);

  @ViewChild('modalContent', { read: ViewContainerRef })
  contentContainer!: ViewContainerRef;

  title = '';
  private _visible = false;
  get visible(): boolean { return this._visible; }
  set visible(value: boolean) {
    const wasVisible = this._visible;
    this._visible = value;
    // PCGL-149: liberar el scroll del fondo apenas el modal pase a oculto,
    // sin importar si fue via close/forceClose/confirm.
    if (wasVisible && !value) BaseModalComponent.releaseBodyScroll(this);
  }
  showHeader = true;
  showFooter = true;
  confirmButtonText = 'Continuar';
  cancelButtonText = 'Cancelar';
  confirmButtonDisabled = false;
  width = '560px';
  customClass = '';
  confirmButtonType: any = 'primary';
  cancelButtonType: any = 'dark-borderless';
  zIndex = 100;
  showCancelButton = true;
  showConfirmButton = true;
  letEvents = false;
  isStatic = true;
  overflowVisible = false;

  nodeStatus = signal<NodeValidationStatus>({ type: 'idle' });
  confirmButtonShake = signal(false);
  messagesExpanded = signal(false);

  closeFn?: () => void;
  confirmFn?: () => void;
  showErrorsFn?: () => void;
  private _isClosing = false;

  constructor(public readonly cdr: ChangeDetectorRef) {
    this.baseModalService.register(this);
  }

  ngOnDestroy(): void {
    this.baseModalService.unregister(this);
    // PCGL-149: si el modal queda visible al destruirlo, restaurar scroll de fondo.
    BaseModalComponent.releaseBodyScroll(this);
  }

  // PCGL-149: tracking de modales que pidieron bloqueo del scroll de fondo.
  // Mientras haya al menos uno activo se mantiene `overflow: hidden` en el body;
  // se restaura solo cuando se cierran todos. Soporta apilamiento de modales.
  private static readonly modalsWithLockedScroll = new Set<BaseModalComponent>();
  private static previousBodyOverflow: string | null = null;

  private static lockBodyScroll(modal: BaseModalComponent): void {
    if (typeof document === 'undefined') return;
    if (BaseModalComponent.modalsWithLockedScroll.size === 0) {
      BaseModalComponent.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    BaseModalComponent.modalsWithLockedScroll.add(modal);
  }

  private static releaseBodyScroll(modal: BaseModalComponent): void {
    if (typeof document === 'undefined') return;
    BaseModalComponent.modalsWithLockedScroll.delete(modal);
    if (BaseModalComponent.modalsWithLockedScroll.size === 0) {
      document.body.style.overflow = BaseModalComponent.previousBodyOverflow ?? '';
      BaseModalComponent.previousBodyOverflow = null;
    }
  }

  open() {
    this.visible = true;
    this.messagesExpanded.set(false);
    BaseModalComponent.lockBodyScroll(this);
    this.cdr.detectChanges();
  }

  async close() {
    if (this._isClosing) return;
    this._isClosing = true;
    try {
      if (this.closeFn) {
        await this.closeFn();
      } else {
        this.visible = false;
      }
      if (!this.visible) BaseModalComponent.releaseBodyScroll(this);
    } finally {
      this._isClosing = false;
    }
  }

  forceClose() {
    this.visible = false;
    this.closeFn?.();
    BaseModalComponent.releaseBodyScroll(this);
  }

  confirm() {
    if (this.confirmButtonDisabled) {
      this.triggerShake();
      return;
    }
    this.confirmFn?.();
  }

  triggerShake() {
    this.confirmButtonShake.set(true);
    setTimeout(() => this.confirmButtonShake.set(false), 500);
    this.cdr.detectChanges();
  }

  toggleMessages() {
    const messages = this.nodeStatus().messages ?? [];
    if (messages.length > 0) {
      this.messagesExpanded.update(v => !v);
      this.showErrorsFn?.();
      this.cdr.detectChanges();
      return;
    }
    this.scrollToFirstError();
  }

  scrollToMessage(index: number) {
    const messages = this.nodeStatus().messages ?? [];
    const message = messages[index];
    const anchor = this.contentContainer?.element?.nativeElement;
    if (!anchor) return;
    let scrollContainer: HTMLElement | null = anchor.parentElement;
    while (scrollContainer && !scrollContainer.classList.contains('overflow-y-auto')) {
      scrollContainer = scrollContainer.parentElement;
    }
    const container = scrollContainer ?? anchor.parentElement;
    if (!container) return;
    let target: HTMLElement | null = null;
    if (message?.anchor) {
      target = container.querySelector(message.anchor) as HTMLElement | null;
    }
    if (!target) {
      target = container.querySelector('[data-validation-warning], [data-validation-error], .border-negative, .field-error') as HTMLElement | null;
    }
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('validation-highlight');
      setTimeout(() => target!.classList.remove('validation-highlight'), 1500);
    }
  }

  scrollToFirstError() {
    // Notificar al componente hijo para que marque todos sus controles como touched
    this.showErrorsFn?.();
    this.cdr.detectChanges();

    const anchor = this.contentContainer?.element?.nativeElement;
    if (!anchor) return;

    // Subir por el DOM hasta encontrar el contenedor scrollable del modal
    let scrollContainer: HTMLElement | null = anchor.parentElement;
    while (scrollContainer && !scrollContainer.classList.contains('overflow-y-auto')) {
      scrollContainer = scrollContainer.parentElement;
    }
    const container = scrollContainer ?? anchor.parentElement;
    if (!container) return;

    setTimeout(() => {
      const isWarning = this.nodeStatus().type === 'warning';
      const selector = isWarning
        ? '[data-validation-warning], .field-error, .border-negative, [data-validation-error]'
        : '.border-negative, .field-error, [data-validation-error], [data-validation-warning]';

      const errorEl = container.querySelector(selector) as HTMLElement | null;
      if (errorEl) {
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorEl.classList.add('validation-highlight');
        setTimeout(() => errorEl.classList.remove('validation-highlight'), 1500);
      }
    }, 50);
  }

  stopPropagation(event: MouseEvent) {
    if (!this.letEvents) {
      event.stopPropagation();
    }
  }

  onBackdropClick(): void {
    this.showErrorsFn?.();
    this.cdr.detectChanges();
  }

  onWrapperClick(): void {
    if (!this.isStatic) this.close();
  }

  onWrapperEscape(): void {
    if (!this.isStatic) this.close();
  }
}